import * as React from 'react'
import {ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {EquipmentsRepository} from './EquipmentsRepository'
import AdminEquipmentForm, {EquipmentFormDataSet} from './AdminEquipmentForm'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../shared/hoc/withAdminPageTemplate'
import {EquipmentForParent} from './EquipmentForParent'
import {Nullable} from '../../app/TypeHelpers'
import {OptionProps} from '../shared/forms/PPSelect'
import {AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../styledComponents/adminPage/Links'
import {DetailsWithInformation} from '../../network/response/DetailsWithInformation'
import Equipment from './Equipment'
import {Messages} from '../../Messages'
import UpdateEquipment from './UpdateEquipment'
import {EquipmentWithChildInfo} from './EquipmentWithChildInfo'

interface Props {
    equipmentsRepo: EquipmentsRepository
    condominiumCd: string
    equipmentId: number
}

interface State {
    isLoadCompleted: boolean
    parentEquipmentsMaster: OptionProps[]
    equipmentForParent: Nullable<EquipmentForParent[]>
    equipmentName: string
    explainText: string
    parentEquipmentId: Nullable<number>
    parentEquipmentName: Nullable<string>
    reservationCountMonthly: Nullable<number>
    reservationCountDaily: Nullable<number>
    lotteryEntryCountMonthly: Nullable<number>
    version: number
    hasChild: boolean
}

class AdminEditEquipmentPage extends React.Component<Props & AdminPagePropsSet, State> {

    static displayName: string = 'AdminEditEquipmentPage'

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)
        this.state = {
            isLoadCompleted: false,
            parentEquipmentsMaster: [],
            equipmentForParent: null,
            equipmentName: '',
            explainText: '',
            parentEquipmentId: null,
            parentEquipmentName: null,
            reservationCountMonthly: null,
            reservationCountDaily: null,
            lotteryEntryCountMonthly: null,
            version: 1,
            hasChild: false,
        }

        this.handleUpdateButtonClick = this.handleUpdateButtonClick.bind(this)
        this.handleUpdateOkClick = this.handleUpdateOkClick.bind(this)
        this.handleUpdateCancelClick = this.handleUpdateCancelClick.bind(this)
        this.requestUpdateEquipment = this.requestUpdateEquipment.bind(this)

        this.handleOnChange = this.handleOnChange.bind(this)
    }

    async loadEquipmentsForParent(): Promise<boolean> {
        const {equipmentsRepo, condominiumCd} = this.props

        const equipmentForParents: Nullable<EquipmentForParent[]> = await this.props.asyncCallFunction(
            equipmentsRepo.getEquipmentsForParent.bind(equipmentsRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
        )
        if (equipmentForParents) {
            this.setState({
                parentEquipmentsMaster: equipmentForParents
                    .filter((item) => item.id !== this.props.equipmentId)
                    .map((equipmentForParentName: EquipmentForParent) => {
                        return ({
                            label: equipmentForParentName.name,
                            value: `${equipmentForParentName.id}`,
                        })
                    }),
                equipmentForParent: equipmentForParents,
            })
            return true
        }
        return false
    }

    async loadEquipment(): Promise<boolean> {
        const {equipmentsRepo, condominiumCd, equipmentId} = this.props

        const equipment: Nullable<EquipmentWithChildInfo> = await this.props.asyncCallFunction(
            equipmentsRepo.getEquipmentById.bind(equipmentsRepo, condominiumCd, equipmentId),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
        )
        if (equipment) {
            this.setState({
                equipmentName: equipment.name,
                explainText: equipment.explainText,
                parentEquipmentId: equipment.parentEquipmentId,
                reservationCountMonthly: equipment.reservationCountMonthly,
                reservationCountDaily: equipment.reservationCountDaily,
                lotteryEntryCountMonthly: equipment.lotteryEntryCountMonthly,
                version: equipment.version,
                hasChild: equipment.hasChild,
            })
            return true
        }
        return false
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        const equipmentLoaded: boolean = await this.loadEquipment()
        const equipmentForParentLoaded: boolean = await this.loadEquipmentsForParent()

        this.setState({
            isLoadCompleted: equipmentForParentLoaded && equipmentLoaded,
        }, () => {
            this.props.eventEmitter.closeLoading()
        })
    }


    needToActivateRegisterButton(): boolean {
        return this.state.equipmentName !== ''
    }


    async requestUpdateEquipment(): Promise<DetailsWithInformation<Equipment>> {
        return await this.props.equipmentsRepo.updateEquipment(
            this.props.condominiumCd,
            this.props.equipmentId,
            new UpdateEquipment(
                this.state.equipmentName,
                this.state.explainText,
                this.state.parentEquipmentId,
                this.state.reservationCountMonthly,
                this.state.reservationCountDaily,
                this.state.lotteryEntryCountMonthly,
                this.state.version,
            ))
    }

    handleUpdateCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleUpdateOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()

        let equipmentAndInformation: Nullable<DetailsWithInformation<Equipment>> = null
        await this.props.eventEmitter.showLoading()
        try {
            equipmentAndInformation = await this.props.asyncCallFunction(
                this.requestUpdateEquipment,
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('設備')},
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        if (equipmentAndInformation) {
            const {condominiumCd} = this.props
            this.props.navigate(
                `/admin/condominiums/${condominiumCd}/equipments/${equipmentAndInformation.details.id}`,
                {
                    state: {
                        initialMessage: {
                            title: equipmentAndInformation.information.title,
                            messageLevel: equipmentAndInformation.information.type,
                            detail: equipmentAndInformation.information.message,
                        },
                    },
                },
            )
        }
    }

    handleUpdateButtonClick(): void {
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0010.errorLevel,
                message: Messages.IOPCC0010.toString('設備'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleUpdateCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleUpdateOkClick,
                    },
                ],
            },
        )
    }

    handleOnChange(value: EquipmentFormDataSet): void {
        this.setState({...value})
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        disabled={!this.needToActivateRegisterButton()}
                        onClick={this.handleUpdateButtonClick}
                    >
                        登録
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/equipments/${this.props.equipmentId}`}
                    >
                        設備詳細へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): ReactNode {
        return (
            <>
                <PageMainArea>
                    {
                        this.state.isLoadCompleted &&
                        <AdminEquipmentForm
                            parentEquipmentsMaster={this.state.parentEquipmentsMaster}
                            equipmentForParent={this.state.equipmentForParent}
                            equipmentData={
                                new EquipmentWithChildInfo(
                                    this.props.equipmentId,
                                    this.state.equipmentName,
                                    this.props.condominiumCd,
                                    this.state.explainText,
                                    this.state.parentEquipmentId,
                                    this.state.parentEquipmentName,
                                    this.state.reservationCountMonthly,
                                    this.state.reservationCountDaily,
                                    this.state.lotteryEntryCountMonthly,
                                    this.state.version,
                                    this.state.hasChild,
                                )}
                            eventEmitter={this.props.eventEmitter}
                            onChange={this.handleOnChange}
                        />
                    }
                </PageMainArea>

                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(AdminEditEquipmentPage)
